import { Eye } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  StatusBadge,
  SkeletonRow,
  formatData,
  formatMoeda,
  useGestaoDocumentosTable,
} from '../GestaoDocumentosProvider';
import type { SimplesNacionalDocumento } from '@/services/SimplesNacional/GestaoDocumentos/api/ListaDocumentosSimplesApi';
import { DocumentoDetalheModal } from './DocumentoDetalheModal';

export type TipoDocumento = 'entradas' | 'saidas';
export type { SimplesNacionalDocumento as DocumentoItem };

interface GestaoDocumentosTableProps {
  tipo: TipoDocumento;
  documentos: SimplesNacionalDocumento[];
  isLoading?: boolean;
}

export function GestaoDocumentosTable({ tipo, documentos, isLoading }: GestaoDocumentosTableProps) {
  const contraparteLabel = tipo === 'entradas' ? 'Fornecedor' : 'Cliente';
  const { selectedDoc, setSelectedDoc } = useGestaoDocumentosTable();

  return (
    <>
      <Table className="bg-white">
        <TableHeader className="bg-[#f7f7f7]">
          <TableRow>
            <TableHead>Número</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>{contraparteLabel}</TableHead>
            <TableHead>CNPJ</TableHead>
            <TableHead className="text-right">Valor Total</TableHead>
            <TableHead className="text-right">Base de Cálculo</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-10">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : documentos.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                Nenhum documento encontrado.
              </TableCell>
            </TableRow>
          ) : (
            documentos.map(doc => (
              <TableRow key={doc.ChaveAcessoDOC}>
                <TableCell className="font-mono text-sm text-primary">{doc.Numero}</TableCell>
                <TableCell className="text-primary whitespace-nowrap">
                  {formatData(doc.Data)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{doc.RazaoSocial || '—'}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {doc.CNPJ || '—'}
                </TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap">
                  {formatMoeda(doc.vNF)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                  {formatMoeda(doc.BaseCalculo)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={doc.Status} />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setSelectedDoc(doc)}
                    aria-label="Ver detalhes"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <DocumentoDetalheModal
        doc={selectedDoc}
        tipo={tipo}
        open={!!selectedDoc}
        onClose={() => setSelectedDoc(null)}
      />
    </>
  );
}
